/**
 * Sinalização e ações das receitas emitidas pela Memed dentro do Klinvia:
 * selo de origem/assinatura e atalhos para a receita digital e o PDF oficial.
 */

import React, { useState } from 'react';
import { Box, Chip, CircularProgress, IconButton, Tooltip } from '@mui/material';
import {
  ContentCopy as CopyIcon,
  OpenInNew as OpenIcon,
  PictureAsPdf as PdfIcon,
  Refresh as RefreshIcon,
  VerifiedUser as SignatureIcon,
} from '@mui/icons-material';
import {
  hasStoredMemedPdf,
  memedErrorMessage,
  openMemedPrescriptionDocument,
  resyncMemedPrescription,
} from '../../services/memedService';

const MEMED_PURPLE = '#5b21b6';

export function isMemedPrescription(prescription: any): boolean {
  return prescription?.origin === 'memed' || Boolean(prescription?.memed_prescription_id);
}

/** Selo de origem: deixa claro que a receita nasceu na Memed e se está assinada. */
export const MemedOriginChip: React.FC<{ prescription: any; size?: 'small' | 'medium' }> = ({
  prescription,
  size = 'small',
}) => {
  if (!isMemedPrescription(prescription)) return null;

  const signed = Boolean(prescription?.memed_signed);
  return (
    <Tooltip
      title={
        signed
          ? 'Emitida na Memed com assinatura digital ICP-Brasil'
          : 'Emitida na Memed — sem assinatura digital'
      }
    >
      <Chip
        size={size}
        icon={<SignatureIcon sx={{ fontSize: 14 }} />}
        label={signed ? 'Memed · assinada' : 'Memed'}
        sx={{
          bgcolor: '#f1f5f9',
          color: signed ? '#64748b' : '#94a3b8',
          fontWeight: 500,
          fontSize: '0.65rem',
          '& .MuiChip-icon': { color: 'inherit' },
        }}
      />
    </Tooltip>
  );
};

export interface MemedDocumentActionsProps {
  prescription: any;
  /** Recebe a receita atualizada (documentos e/ou itens) para atualizar a linha na lista. */
  onUpdated?: (changes: Record<string, any>) => void;
  onError?: (message: string) => void;
  onSuccess?: (message: string) => void;
}

/** Receita digital (link do paciente), PDF oficial e recuperação dos documentos. */
export const MemedDocumentActions: React.FC<MemedDocumentActionsProps> = ({
  prescription,
  onUpdated,
  onError,
  onSuccess,
}) => {
  const [refreshing, setRefreshing] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!isMemedPrescription(prescription)) return null;

  const link: string | null = prescription?.memed_digital_link || null;
  const pdfUrl: string | null = prescription?.memed_pdf_url || null;
  const unlockCode: string | null = prescription?.memed_unlock_code || null;
  const storedPdf = hasStoredMemedPdf(prescription);
  const itemsKnown = Array.isArray(prescription?.items) || typeof prescription?.item_count === 'number';
  const missingItems = itemsKnown
    && (Array.isArray(prescription?.items) ? prescription.items.length : prescription.item_count) === 0;

  const handleResync = async () => {
    setRefreshing(true);
    try {
      const result = await resyncMemedPrescription(prescription.id);
      if (result.prescription) {
        const items = Array.isArray(result.prescription.items) ? result.prescription.items : undefined;
        onUpdated?.({ ...result.prescription, ...(items ? { item_count: items.length } : {}) });
      }
      const parts = [
        result.items_synced > 0 ? `${result.items_synced} item(ns) sincronizado(s)` : '',
        result.pdf_stored ? 'PDF guardado no Klinvia' : '',
      ].filter(Boolean);
      if (parts.length > 0) onSuccess?.(`Receita Memed sincronizada: ${parts.join(' e ')}.`);
      if (result.documents_error && !result.pdf_stored) {
        onError?.(`O PDF ainda não está disponível na Memed: ${result.documents_error}`);
      }
    } catch (error: any) {
      onError?.(memedErrorMessage(error, 'Não foi possível sincronizar a receita com a Memed.'));
    } finally {
      setRefreshing(false);
    }
  };

  const handleOpenPdf = async () => {
    const result = await openMemedPrescriptionDocument(prescription);
    if (result.documents) onUpdated?.(result.documents);
    if (!result.opened) onError?.(result.message || 'Não foi possível abrir o PDF da receita.');
  };

  const handleCopy = async () => {
    if (!link) return;
    const text = unlockCode ? `${link} (código: ${unlockCode})` : link;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      onError?.('Não foi possível copiar o link.');
    }
  };

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
      {link && (
        <>
          <Tooltip
            title={
              unlockCode
                ? `Abrir receita digital do paciente (código ${unlockCode})`
                : 'Abrir receita digital do paciente'
            }
          >
            <IconButton
              size="small"
              onClick={() => window.open(link, '_blank', 'noopener,noreferrer')}
              sx={{ color: '#94a3b8' }}
            >
              <OpenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={copied ? 'Link copiado!' : 'Copiar link da receita digital'}>
            <IconButton size="small" onClick={() => void handleCopy()} sx={{ color: '#94a3b8' }}>
              <CopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )}

      {(storedPdf || pdfUrl) && (
        <Tooltip title={storedPdf ? 'Abrir PDF da receita (guardado no Klinvia)' : 'Abrir PDF oficial da Memed'}>
          <IconButton size="small" onClick={() => void handleOpenPdf()} sx={{ color: '#b91c1c' }}>
            <PdfIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {(!storedPdf || missingItems) && (
        <Tooltip
          title={
            missingItems
              ? 'Sincronizar com a Memed: recuperar medicamentos e PDF'
              : 'Sincronizar com a Memed: guardar o PDF no Klinvia'
          }
        >
          <IconButton size="small" onClick={() => void handleResync()} disabled={refreshing}>
            {refreshing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

/**
 * Abre o documento oficial de uma receita Memed a partir dos impressos da
 * evolução, onde só temos o id da receita. A URL é sempre relida no backend:
 * assim o botão funciona mesmo em receitas cujo PDF foi publicado depois.
 */
export const MemedOfficialDocumentButton: React.FC<{
  prescriptionId: string;
  onError?: (message: string) => void;
}> = ({ prescriptionId, onError }) => {
  const [loading, setLoading] = useState(false);

  const handleOpen = async () => {
    setLoading(true);
    try {
      const result = await openMemedPrescriptionDocument({ id: prescriptionId });
      if (!result.opened) {
        onError?.(result.message || 'Não foi possível abrir o documento da Memed.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip title="Abrir receita digital assinada (Memed)">
      <IconButton
        size="small"
        disabled={loading}
        onClick={(e) => {
          e.stopPropagation();
          void handleOpen();
        }}
        sx={{ color: '#94a3b8', flexShrink: 0 }}
        aria-label="Abrir receita digital assinada na Memed"
      >
        {loading ? <CircularProgress size={16} /> : <OpenIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
};

export default MemedOriginChip;
